import "@/../css/confetti.css";
import { confetti, ConfettiOptions } from "@tsparticles/confetti";

export const DEFAULT_CONFETTI_DURATION_MS = 500;
export const MAX_CONFETTI_DURATION_MS = 3_000;
export const CONFETTI_EMISSION_INTERVAL_MS = 1_000 / 30;
export const CONFETTI_SETTLE_CHECK_MS = 1_000;

const PARTICLES_PER_SIDE_PER_EMISSION = 4;

type ConfettiContainer = Awaited<ReturnType<typeof confetti>>;

let confettiContainer: ConfettiContainer;
let runningBursts = 0;
let pendingEmissions = 0;
let settleCheckTimeout: number | undefined;

/**
 * Peer messages arrive as unvalidated JSON off the data channel and the burst
 * keeps emitting until its duration elapses, so an absurd value
 * would tie up the other participant's browser for as long as it liked.
 */
export function clampConfettiDuration(durationMs?: number): number {
    if (typeof durationMs !== "number" || !Number.isFinite(durationMs)) {
        return DEFAULT_CONFETTI_DURATION_MS;
    }
    return Math.min(Math.max(durationMs, 0), MAX_CONFETTI_DURATION_MS);
}

function emit(options: ConfettiOptions) {
    pendingEmissions++;
    void confetti(options)
        .then((container) => {
            confettiContainer = container ?? confettiContainer;
        })
        .finally(() => {
            pendingEmissions--;
        });
}

function destroyConfettiOnceSettled() {
    window.clearTimeout(settleCheckTimeout);
    settleCheckTimeout = window.setTimeout(() => {
        if (runningBursts > 0) {
            return;
        }
        const container = confettiContainer;
        const hasParticlesLeft =
            !!container &&
            !container.destroyed &&
            container.particles.count > 0;
        if (pendingEmissions > 0 || document.hidden || hasParticlesLeft) {
            destroyConfettiOnceSettled();
            return;
        }
        // tsparticles never stops its full-screen redraw loop on its own, even with no particles left.
        container?.destroy();
    }, CONFETTI_SETTLE_CHECK_MS);
}

export default function playConfetti(
    props?: ConfettiOptions,
    durationMs: number = DEFAULT_CONFETTI_DURATION_MS,
) {
    const endsAt = Date.now() + durationMs;
    let nextEmissionAt = Date.now();
    runningBursts++;

    (function frame() {
        if (Date.now() >= nextEmissionAt) {
            emit({
                particleCount: PARTICLES_PER_SIDE_PER_EMISSION,
                angle: 60,
                spread: 55,
                origin: { x: 0 },
                ...props,
            });
            emit({
                particleCount: PARTICLES_PER_SIDE_PER_EMISSION,
                angle: 120,
                spread: 55,
                origin: { x: 1 },
                ...props,
            });
            nextEmissionAt += CONFETTI_EMISSION_INTERVAL_MS;
        }

        if (Date.now() < endsAt) {
            requestAnimationFrame(frame);
            return;
        }

        runningBursts--;
        destroyConfettiOnceSettled();
    })();
}

const MEGA_CONFETTI_OPTIONS: ConfettiOptions = {
    ticks: 500,
    scalar: 3,
};

/**
 * The oversized burst the goals board uses when a goal reaches "Accomplished".
 * Particles are 3x normal and live far longer, so the celebration keeps going
 * well past the emission window.
 */
export function playMegaConfetti(durationMs?: number) {
    playConfetti(MEGA_CONFETTI_OPTIONS, durationMs);
}
